import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowUpRight } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { FadeUp, MaskLines } from "../components/Reveal";

const PROJECT_TYPES = ["Private Residence", "Apartment Interior", "Commercial Space", "Hospitality", "Renovation", "Other"];
const BUDGETS = ["Under ₹50 Lakh", "₹50 Lakh – ₹2 Crore", "₹2 Crore +", "Not sure yet"];

const SOCIALS = [
  { key: "instagram", label: "Instagram" },
  { key: "linkedin", label: "LinkedIn" },
  { key: "pinterest", label: "Pinterest" },
  { key: "facebook", label: "Facebook" },
  { key: "youtube", label: "YouTube" },
  { key: "whatsapp", label: "WhatsApp" },
];

const inputCls =
  "w-full bg-transparent border-b border-[#181816]/20 py-3.5 font-light text-lg placeholder:text-[#8A857D]/70 focus:border-[#181816] transition-colors duration-300";

export default function Contact() {
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", project_type: PROJECT_TYPES[0], budget: BUDGETS[3], message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/inquiries", form);
      toast.success("Inquiry received — Ak will write back personally.");
      setForm({ name: "", email: "", phone: "", project_type: PROJECT_TYPES[0], budget: BUDGETS[3], message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <main className="pt-28 sm:pt-40 px-6 sm:px-10 pb-24 sm:pb-32">
      <div className="max-w-[1400px] mx-auto">
        <FadeUp>
          <p className="flex items-center gap-3 font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.25em] text-[#8A857D]">
            <span className="h-px w-10 bg-[#C29B38]" /> Commissions
          </p>
        </FadeUp>
        <MaskLines
          as="h1"
          delay={0.1}
          className="mt-5 font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.05]"
          lines={["Tell us about", { node: <span><em className="italic">your space</em>.</span> }]}
        />

        <div className="mt-16 grid md:grid-cols-12 gap-16">
          <div className="md:col-span-5">
            <FadeUp>
              <div className="flex flex-col gap-8">
                {settings?.email && (
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#8A857D]">Email</p>
                    <a href={`mailto:${settings.email}`} data-testid="contact-email" className="link-underline mt-2 inline-block font-serif text-xl sm:text-2xl">{settings.email}</a>
                  </div>
                )}
                {settings?.phone && (
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#8A857D]">Phone</p>
                    <p className="mt-2 font-serif text-xl sm:text-2xl">{settings.phone}</p>
                  </div>
                )}
                {settings?.address && (
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#8A857D]">Studio</p>
                    <p className="mt-2 font-light text-[#57534E] leading-relaxed">{settings.address}</p>
                  </div>
                )}
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#8A857D]">Follow</p>
                  <div className="mt-3 flex flex-col gap-2" data-testid="contact-socials">
                    {SOCIALS.filter((s) => settings?.[s.key]).map((s) => (
                      <a key={s.key} href={settings[s.key]} target="_blank" rel="noopener noreferrer" data-testid={`contact-social-${s.key}`} className="group w-fit inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-[#57534E] hover:text-[#181816] transition-colors">
                        {s.label}
                        <ArrowUpRight className="h-3 w-3 opacity-40 group-hover:opacity-100 transition-opacity" />
                      </a>
                    ))}
                  </div>
                </div>
                <p className="font-light text-[#8A857D] leading-relaxed max-w-xs">
                  The studio takes on a limited number of commissions each year — early conversations are always welcome.
                </p>
              </div>
            </FadeUp>
          </div>

          <div className="md:col-span-7">
            <FadeUp delay={0.1}>
              <form onSubmit={submit} className="flex flex-col gap-8" data-testid="inquiry-form">
                <div className="grid sm:grid-cols-2 gap-8">
                  <input required value={form.name} onChange={set("name")} placeholder="Your name *" data-testid="inquiry-name-input" className={inputCls} />
                  <input required type="email" value={form.email} onChange={set("email")} placeholder="Email address *" data-testid="inquiry-email-input" className={inputCls} />
                </div>
                <div className="grid sm:grid-cols-2 gap-8">
                  <input value={form.phone} onChange={set("phone")} placeholder="Phone / WhatsApp" data-testid="inquiry-phone-input" className={inputCls} />
                  <select value={form.project_type} onChange={set("project_type")} data-testid="inquiry-service-select" className={inputCls + " cursor-pointer"}>
                    {PROJECT_TYPES.map((t) => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#8A857D] mb-3">Budget range</p>
                  <select value={form.budget} onChange={set("budget")} data-testid="inquiry-budget-select" className={inputCls + " cursor-pointer"}>
                    {BUDGETS.map((b) => <option key={b}>{b}</option>)}
                  </select>
                </div>
                <textarea required rows={5} value={form.message} onChange={set("message")} placeholder="Tell us about the site, the rooms, and the stillness you are looking for… *" data-testid="inquiry-message-input" className={inputCls + " resize-none"} />
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="inquiry-form-submit"
                  className="group w-fit inline-flex items-center gap-3 bg-[#181816] text-[#F8F6F0] px-8 py-4 font-mono text-[11px] uppercase tracking-[0.22em] hover:bg-[#B85D3B] disabled:opacity-50 transition-colors duration-300"
                >
                  {sending ? "Sending…" : "Send Inquiry"}
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </button>
              </form>
            </FadeUp>
          </div>
        </div>
      </div>
    </main>
  );
}
